import React, { useState } from 'react';
import Loading from './Loading';
import TopBar from './TopBar';

export default function LoadingDemo() {
    const [selectedVariant, setSelectedVariant] = useState('default');
    const [selectedSize, setSelectedSize] = useState('medium');
    const [showMessage, setShowMessage] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const variants = ['default', 'pulse', 'dots', 'heartbeat', 'medical'];
    const sizes = ['small', 'medium', 'large'];

    return (
        <div className="min-h-screen bg-gray-50">
            <TopBar title="Loading Component Demo" url="/" />
            
            <div className="pt-16 px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">
                            Enhanced Loading Component
                        </h1>
                        <p className="text-gray-600">
                            Modern, healthcare-themed loading indicators with multiple variants
                        </p>
                    </div>

                    {/* Controls */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                        <h2 className="text-xl font-semibold mb-4">Customize Loading</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Variant Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Variant
                                </label>
                                <select
                                    value={selectedVariant}
                                    onChange={(e) => setSelectedVariant(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary100 focus:border-transparent"
                                >
                                    {variants.map(variant => (
                                        <option key={variant} value={variant}>
                                            {variant.charAt(0).toUpperCase() + variant.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Size Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Size
                                </label>
                                <select
                                    value={selectedSize}
                                    onChange={(e) => setSelectedSize(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary100 focus:border-transparent"
                                >
                                    {sizes.map(size => (
                                        <option key={size} value={size}>
                                            {size.charAt(0).toUpperCase() + size.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Message Toggle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Show Message
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={showMessage}
                                        onChange={(e) => setShowMessage(e.target.checked)}
                                        className="rounded border-gray-300 text-primary100 focus:ring-primary100"
                                    />
                                    <span className="ml-2 text-sm text-gray-600">Include loading message</span>
                                </label>
                            </div>

                            {/* Full Screen Toggle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Screen
                                </label>
                                <button
                                    onClick={() => setFullScreen(true)}
                                    className="w-full bg-primary100 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 transition-colors"
                                >
                                    Test Full Screen
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
                        <h2 className="text-xl font-semibold mb-6 text-center">Preview</h2>
                        <div className="min-h-[200px] flex items-center justify-center bg-gray-50 rounded-lg">
                            <Loading
                                variant={selectedVariant}
                                size={selectedSize}
                                message={showMessage ? "Loading your healthcare data..." : ""}
                            />
                        </div>
                    </div>

                    {/* All Variants Showcase */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="text-xl font-semibold mb-6">All Variants</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {variants.map(variant => (
                                <div key={variant} className="text-center">
                                    <h3 className="font-medium text-gray-700 mb-4 capitalize">
                                        {variant}
                                    </h3>
                                    <div className="bg-gray-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center">
                                        <Loading variant={variant} size="medium" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Usage Examples */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
                        <h2 className="text-xl font-semibold mb-4">Usage Examples</h2>
                        <div className="space-y-4 text-sm">
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <p className="font-medium mb-2">Basic Usage (Backward Compatible):</p>
                                <code className="text-primary100">{`<Loading />`}</code>
                            </div>
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <p className="font-medium mb-2">With Message:</p>
                                <code className="text-primary100">{`<Loading message="Loading appointments..." />`}</code>
                            </div>
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <p className="font-medium mb-2">Medical Variant:</p>
                                <code className="text-primary100">{`<Loading variant="medical" size="large" />`}</code>
                            </div>
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <p className="font-medium mb-2">Full Screen Loading:</p>
                                <code className="text-primary100">{`<Loading variant="heartbeat" fullScreen message="Processing..." />`}</code>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Full Screen Loading Demo */}
            {fullScreen && (
                <Loading
                    variant="medical"
                    size="large"
                    message="Testing full screen loading..."
                    fullScreen
                />
            )}

            {/* Auto-close full screen demo */}
            {fullScreen && setTimeout(() => setFullScreen(false), 3000)}
        </div>
    );
}